import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

const SCRAMBLE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#/\\{}<>$&%";

/**
 * Frames between re-rolls of the unsettled characters. Rolling on every frame
 * at 60fps reads as flicker rather than as scrambling.
 */
const ROLL_EVERY = 3;

const randomChar = () => SCRAMBLE[Math.floor(Math.random() * SCRAMBLE.length)];

/**
 * Characters scramble then resolve left-to-right — a decode, which is what the
 * product does to a document.
 *
 * Accessibility: the real string is exposed once via an `aria-label` and the
 * animating span is hidden from the accessibility tree, so screen readers never
 * hear the noise. Reduced motion renders the final text with no animation.
 * Text is written straight to the DOM node to avoid a setState per frame.
 */
function DecodeText({ text, speed = 5, delay = 0, className = "", as: Tag = "span" }) {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (prefersReducedMotion) {
      node.textContent = text;
      return undefined;
    }

    let raf = 0;
    let timer = 0;
    let frame = 0;
    const chars = Array.from(text);
    let noise = chars.map(randomChar);

    const render = (settled) => {
      node.textContent = chars
        .map((ch, i) => (ch === " " ? " " : i < settled ? ch : noise[i]))
        .join("");
    };

    const tick = () => {
      frame += 1;
      const settled = Math.floor(frame / speed);
      if (frame % ROLL_EVERY === 0) noise = chars.map(randomChar);
      render(settled);

      if (settled <= chars.length) {
        raf = requestAnimationFrame(tick);
      } else {
        node.textContent = text;
      }
    };

    // Hold random noise during the delay rather than an empty gap or a row of
    // the same letter.
    render(0);
    timer = window.setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, delay * 1000);

    return () => {
      window.clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [text, speed, delay, prefersReducedMotion]);

  return (
    <Tag className={className} aria-label={text}>
      <span ref={ref} aria-hidden="true">
        {text}
      </span>
    </Tag>
  );
}

export default DecodeText;
